"use client"

import * as React from "react"
import { format, parseISO, addDays, addMonths, addYears } from "date-fns"
// Locale-aware chrono imports
import { uk, en } from "chrono-node"
import { isDayFirstLocale } from "./date-picker-locale"
import { CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cleanDateAndTimeObject, toLocalDateString } from "@shared/renderDate"

interface DatePickerProps {
  date: Date | undefined
  setDate: (date: Date | undefined) => void
  onChange?: (date: Date | undefined) => void
  readOnly?: boolean
  placeholder?: string
  fullWidth?: boolean
  enableNaturalInput?: boolean
  showPreview?: boolean
  label?: string
  className?: string
}

export function DatePicker({
  date,
  setDate,
  onChange,
  readOnly = false,
  placeholder = "Pick a date",
  fullWidth = false,
  enableNaturalInput = true,
  showPreview = true,
  label,
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [month, setMonth] = React.useState<Date | undefined>(date)

  // Get today's date with the current local time
  const today = React.useMemo(() => {
    const now = new Date()
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      now.getHours(),
      now.getMinutes(),
      now.getSeconds(),
      now.getMilliseconds()
    )
  }, [])

  // Create a local date object that correctly reflects the user's timezone
  const ensureLocalDate = (date?: Date): Date | undefined => {
    if (!date) return undefined
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    )
  }

  // Format date for display using local timezone
  const formatLocalDate = (date?: Date): string => {
    if (!date) return ""
    return format(ensureLocalDate(date) || date, "PPP")
  }

  // Handle date selection to ensure local dates
  const handleSelect = (newDate: Date | undefined) => {
    const localDate = newDate ? ensureLocalDate(newDate) : undefined
    setDate(localDate)
    setMonth(localDate || new Date())
    // If natural input is enabled, update the input field to match the selected date
    if (enableNaturalInput) {
      setInputValue(localDate ? formatLocalDate(localDate) : "")
    }
    setOpen(!open)
  }

  // Handle natural language input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Always update the input value as the user types
    setInputValue(value);

    if (enableNaturalInput && value.trim()) {
      // Use locale-aware parser for natural language input

      const useDayFirst = isDayFirstLocale();
      const parser = useDayFirst ? uk : en;

      // Try parsing with the selected parser
      let parsedDate = parser.parseDate(value, undefined, { forwardDate: true });

      // If no date found with the primary parser, try the other one as fallback
      if (!parsedDate) {
        const fallbackParser = useDayFirst ? en : uk;
        parsedDate = fallbackParser.parseDate(value, undefined, { forwardDate: true });
      }


      if (parsedDate) {
        const localDate = cleanDateAndTimeObject(parsedDate);

        // Update the date state
        // Add current time to the date
        localDate.setHours(today.getHours());
        localDate.setMinutes(today.getMinutes());
        localDate.setSeconds(today.getSeconds());
        localDate.setMilliseconds(today.getMilliseconds());
        setDate(localDate);
        setMonth(localDate);

        // Format the date for display
        const formattedDate = formatLocalDate(localDate);
        setInputValue(formattedDate);

        // Notify parent component of the change
        if (onChange) {
          onChange(localDate);
        }

        console.log('[DatePicker] values:', {
          inputValue: value,
          parsedDate,
          localDate,
          formattedValue: formattedDate
        });
      } else if (date) {
        // Only clear the date if we had a previous date and now can't parse
        setDate(undefined);
        if (onChange) {
          onChange(undefined);
        }
      }
    } else if (!value.trim()) {
      // Clear date if input is empty
      setDate(undefined);
      if (onChange) {
        onChange(undefined);
      }
    }
  }

  // Handle calendar month change
  const handleMonthChange = (newMonth: Date) => {
    setMonth(newMonth)
  }

  // Sync internal state when the controlled date changes
  React.useEffect(() => {
    if (!date) {
      if (!enableNaturalInput && inputValue) {
        setInputValue("")
      }
      return
    }

    const formatted = formatLocalDate(date)
    if (formatted !== inputValue) {
      setInputValue(formatted)
    }
    setMonth(date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date])

  const renderInput = () => {
    if (enableNaturalInput) {
      return (
        <div className="relative flex flex-col gap-2 w-full">
          {label && <Label className="px-1">{label}</Label>}
          <div className="relative flex gap-2 w-full">
            <Input
              value={inputValue}
              placeholder={placeholder}
              className={cn("pr-10", fullWidth && "w-full")}
              onChange={handleInputChange}
              onClick={(e) => {
                // Prevent the calendar from opening when clicking the input in natural language mode
                e.stopPropagation()
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault()
                  setOpen(true)
                } else if (e.key === "Enter") {
                  e.preventDefault() // Prevent form submission on Enter
                }
              }}
              readOnly={readOnly}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setOpen(!open)
              }}
              disabled={readOnly}
            >
              <CalendarIcon className="h-3.5 w-3.5" />
              <span className="sr-only">Select date</span>
            </Button>
          </div>
        </div>
      )
    }

    return (
      <Button
        type="button"
        variant="outline"
        className={cn(
          fullWidth ? "w-full" : "w-[240px]",
          "justify-start text-left font-normal",
          !date && "text-muted-foreground",
          readOnly && "pointer-events-none opacity-70",
          className
        )}
        tabIndex={readOnly ? -1 : 0}
        onClick={() => setOpen(true)}
      >
        <CalendarIcon className="mr-2 h-4 w-4" />
        {date ? formatLocalDate(date) : <span>{placeholder}</span>}
      </Button>
    )
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {renderInput()}
        </PopoverTrigger>
        {!readOnly && (
          <PopoverContent className="w-auto p-0" align="start" data-slot="popover-content">
            <div className="flex flex-col gap-4">
              <Calendar
                mode="single"
                selected={cleanDateAndTimeObject(date)}
                onSelect={handleSelect}
                month={month}
                onMonthChange={handleMonthChange}
                initialFocus
                today={today}
                fromDate={new Date(2020, 0, 1)}
                defaultMonth={date || today}
              />
              <div className="border-t border-border pt-3 px-3 mb-2">                
                <p className="text-xs text-muted-foreground mb-2">Add time from today ({formatLocalDate(today)}):</p>
                <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "3 months", months: 3 },
                  { label: "6 months", months: 6 },
                  { label: "1 year", years: 1 },
                  { label: "2 years", years: 2 },
                  { label: "3 years", years: 3 },
                  { label: "5 years", years: 5 },
                ].map((preset) => (
                  <Button
                    key={preset.label}
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs justify-center"
                    onClick={(e) => {
                      e.preventDefault()
                      // Calculate new date using either months or years
                      let newDate;
                      if ('months' in preset) {
                        newDate = addMonths(new Date(), preset.months);
                      } else {
                        newDate = addYears(new Date(), preset.years);
                      }
                      const localDate = ensureLocalDate(newDate)
                      setDate(localDate)
                      setMonth(localDate)
                      if (enableNaturalInput) {
                        // Use formatted date instead of preset label
                        setInputValue(toLocalDateString(cleanDateAndTimeObject(localDate)))
                      }
                      // Call onChange to notify parent component
                      if (onChange) {
                        onChange(localDate)
                      }
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
                </div>
              </div>
            </div>
          </PopoverContent>
        )}
      </Popover>
      {showPreview && date && (
        <div className="text-muted-foreground px-1 text-sm">
          Selected date: <span className="font-medium">{toLocalDateString(cleanDateAndTimeObject(date))}</span>
        </div>
      )}
    </div>
  )
}
